import React, { useState } from 'react'
import DashboardDevicesMethods, { DeviceStatus } from './dashboard__devices.local.methods'
import Card from 'blocks.simple/card/card'
import Animation from 'blocks.simple/animation/animation'
import Link from 'blocks.simple/link/link'
import translate from 'core/translate'
import { routes } from 'features/routes'
import helpers from 'core/helpers'
import styles from './dashboard__devices.jcss'
import { Typography } from 'atoms/Typography'
import { getURLSearchParamsString } from 'features/routes/utils'
import { cn } from 'ethcss'
import { Switch } from '../../../atoms/Switch'
import { LinkProps } from 'blocks.simple/link/link.types'

const displaysQueryParam = helpers.prefixQuery({ name: 'displays', field: 'query' })

const linkRoutes: Record<DeviceStatus, NonNullable<LinkProps['to']>> = {
    online: {
        pathname: routes.displays.path,
        search: getURLSearchParamsString({ [displaysQueryParam]: 'online' }),
    },
    offline: {
        pathname: routes.displays.path,
        search: getURLSearchParamsString({ [displaysQueryParam]: 'offline' }),
    },
    error: {
        pathname: routes.displays.path,
        search: getURLSearchParamsString({ [displaysQueryParam]: 'error' }),
    },
    virtual: {
        pathname: routes.displays.path,
        search: getURLSearchParamsString({
            displays__filterList: JSON.stringify([
                {
                    name: 'platform',
                    type: 'select',
                    translateOptionsQ: false,
                    options: [
                        {
                            value: null,
                            label: 'null',
                            selected: true,
                        },
                    ],
                },
            ]),
        }),
    },
    sleep: {
        pathname: routes.displays.path,
        search: getURLSearchParamsString({ [displaysQueryParam]: 'sleep' }),
    },
    draft: {
        pathname: routes.draftDevices.path,
    },
}

interface IDashboardDevicesProps {
    className?: string
    rules?: {
        order: number
    }
}

function DashboardDevices(p_: IDashboardDevicesProps) {
    const [withVirtualDevices, setWithVirtualDevices] = useState(true)
    const { s_ } = DashboardDevicesMethods()
    const itemOrder = p_.rules?.order ?? 0

    const getStatus = (status: DeviceStatus, widthValue: number) => {
        if (!s_.data.stats) {
            return null
        }

        const statusOrder: DeviceStatus[] = ['online', 'sleep', 'error', 'offline', 'virtual', 'draft']
        const getStatusValue = (currentStatus: DeviceStatus) =>
            s_.data.stats?.find(({ key }) => key === currentStatus)?.value ?? 0
        const shareArray = toPercentShare(
            statusOrder.map((currentStatus) =>
                currentStatus === 'virtual' && !withVirtualDevices ? 0 : getStatusValue(currentStatus)
            )
        )
        const statusIndex = statusOrder.indexOf(status)
        const item = s_.data.stats.find((stat) => stat.label === status)

        return item ? (
            <div
                className={cn(styles.statusBlock, widthValue === 0.5 ? styles.halfWidth : styles.fullWidth)}
                style={{
                    backgroundColor: item.color,
                }}
            >
                <div>
                    <Typography type="text" className={styles.title}>
                        {translate(item.label)}
                        {status === 'virtual' && !withVirtualDevices ? null : <span>({shareArray[statusIndex]}%)</span>}
                    </Typography>
                </div>
                <div className={styles.info}>
                    <Link className={styles.link} to={linkRoutes[item.key]}>
                        {item.value}
                    </Link>
                </div>
            </div>
        ) : null
    }

    function toPercentShare(values: number[]) {
        if (!Array.isArray(values)) throw new TypeError('Expected an array')
        if (values.length === 0) return []

        const numericValues = values.map(Number)
        if (!numericValues.every(Number.isFinite)) throw new TypeError('Array must contain only finite numbers')

        const total = numericValues.reduce((sum, value) => sum + value, 0)
        if (total === 0) return numericValues.map(() => 0)

        const exactShares = numericValues.map((value) => (value / total) * 100)
        const integerShares = exactShares.map(Math.floor)
        const missingPercent = 100 - integerShares.reduce((sum, value) => sum + value, 0)

        const indexesByRemainder = exactShares
            .map((value, index) => ({
                index,
                remainder: value - Math.floor(value),
            }))
            .sort((a, b) => b.remainder - a.remainder || a.index - b.index)

        for (let index = 0; index < missingPercent; index++) {
            integerShares[indexesByRemainder[index].index] += 1
        }

        return integerShares
    }

    function renderCard() {
        if (!s_.data.stats) {
            return null
        }

        return (
            <Card
                title={translate('deviceSummary').toUpperCase()}
                icon={{
                    type: 'display',
                    size: 20,
                    default: true,
                }}
            >
                <div className={styles.wrapper}>
                    {getStatus('online', 0.5)}
                    {getStatus('sleep', 0.5)}
                    {getStatus('error', 0.5)}
                    {getStatus('offline', 0.5)}
                    {getStatus('virtual', 0.5)}
                    {getStatus('draft', 0.5)}
                </div>
                <div className={styles.virtualSwitch}>
                    <Switch onChange={() => setWithVirtualDevices((value) => !value)} checked={withVirtualDevices} />
                    <span>{translate('withVirtual')}</span>
                </div>
                <Typography type="text" className={styles.footer}>
                    {translate('devicesInCompany')}: {s_.data.devicesCount}
                </Typography>
            </Card>
        )
    }

    return (
        <Animation className={cn(p_.className)} style={{ order: itemOrder }}>
            {renderCard()}
        </Animation>
    )
}

export default React.memo(DashboardDevices)
